<template>
  <div class="h-full w-full min-h-[500px]">
    <VisualStudyDesignBuilder
      :mode="disabled ? 'select' : 'edit'"
      v-model:populations="localPopulations"
      v-model:arms="localArms"
      v-model:exposures="localExposures"
      v-model:interventions="localInterventions"
      v-model:global-criteria="localGlobalCriteria"
      v-model:global-characteristics="localGlobalCharacteristics"
      v-model:baseline-name="localBaselineName"
    >
      <template #sync-status>
        <button
          v-if="saveState === 'dirty'"
          class="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800 transition-colors px-2 py-1 rounded hover:bg-amber-50"
          title="Click to save now"
          @click="saveNow"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
          Unsaved changes
        </button>
        <span
          v-else-if="saveState === 'saving'"
          class="flex items-center gap-1.5 text-xs text-slate-500 px-2"
        >
          <svg
            class="animate-spin h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Saving…
        </span>
        <span
          v-else-if="saveState === 'synced'"
          class="flex items-center gap-1 text-xs text-green-600 px-2"
        >
          <svg
            class="h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
              clip-rule="evenodd"
            />
          </svg>
          Saved
        </span>
      </template>
    </VisualStudyDesignBuilder>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { VisualStudyDesignBuilder } from "visual-study-design-builder";
import "visual-study-design-builder/style.css";
import type {
  PopulationDataEntity,
  ArmDataEntity,
  Exposure,
  Intervention,
} from "visual-study-design-builder";
import type { ReviewItem } from "@core/domain/models/reviewItem.model";
import type { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import type { StudyDesignValue } from "@core/domain/types/attributeValue.type";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { errorMessage } from "@app/utils/error-message";

const props = defineProps<{
  study: ReviewItem;
  attributeStructure: AttributeStructure;
  disabled?: boolean;
}>();

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

const localPopulations = ref<PopulationDataEntity[]>([]);
const localArms = ref<ArmDataEntity[]>([]);
const localExposures = ref<Exposure[]>([]);
const localInterventions = ref<Intervention[]>([]);
const localGlobalCriteria = ref<string[]>([]);
const localGlobalCharacteristics = ref<string[]>([]);
const localBaselineName = ref<string>("Baseline");

let skipServerLoadUntil = 0;
const SKIP_DURATION = 3000;
let isLoadingFromServer = false;

// ── save state ────────────────────────────────────────────────────────────────
// changeId increments on every local mutation; savedId is set to changeId on
// each successful server save. State is derived from these two counters plus
// a saveActive mutex so concurrent async paths never clobber each other.
const changeId = ref(0);
const savedId = ref(0);
const isSaving = ref(false);
const saveEverSucceeded = ref(false);
let saveActive = false;

type SaveState = "idle" | "dirty" | "saving" | "synced";
const saveState = computed<SaveState>(() => {
  if (isSaving.value) return "saving";
  if (changeId.value > savedId.value) return "dirty";
  if (saveEverSucceeded.value) return "synced";
  return "idle";
});

// ── load from server ──────────────────────────────────────────────────────────
function loadFromServer() {
  if (Date.now() < skipServerLoadUntil) return;

  isLoadingFromServer = true;
  const attr = currentAttribute.value;
  if (attr) {
    const value = attr.value as StudyDesignValue | undefined;
    localPopulations.value = (value?.populations ?? []) as PopulationDataEntity[];
    localArms.value = (value?.arms ?? []) as ArmDataEntity[];
    localExposures.value = (value?.exposures ?? []) as Exposure[];
    localInterventions.value = (value?.interventions ?? []).map((i) => ({
      ...i,
      timepoints: i.timepoints ?? [],
    })) as Intervention[];
    localGlobalCriteria.value = value?.globalCriteria ?? [];
    localGlobalCharacteristics.value = value?.globalCharacteristics ?? [];
    localBaselineName.value = value?.baselineName ?? "Baseline";
  } else {
    localPopulations.value = [];
    localArms.value = [];
    localExposures.value = [];
    localInterventions.value = [];
    localGlobalCriteria.value = [];
    localGlobalCharacteristics.value = [];
    localBaselineName.value = "Baseline";
  }
  // Reset the flag on the next microtask so watchers triggered by the
  // assignments above see isLoadingFromServer = true and skip syncToServer.
  Promise.resolve().then(() => {
    isLoadingFromServer = false;
  });
}

watch(
  () => props.study.id,
  () => {
    skipServerLoadUntil = 0;
    changeId.value = 0;
    savedId.value = 0;
    saveEverSucceeded.value = false;
    loadFromServer();
  },
  { immediate: true },
);

watch(
  () => currentAttribute.value,
  () => {
    loadFromServer();
  },
  { deep: true },
);

// ── save ──────────────────────────────────────────────────────────────────────
function buildCurrentValue(): StudyDesignValue {
  return {
    populations: localPopulations.value,
    arms: localArms.value,
    exposures: localExposures.value,
    interventions: localInterventions.value,
    globalCriteria: localGlobalCriteria.value,
    globalCharacteristics: localGlobalCharacteristics.value,
    baselineName: localBaselineName.value,
  };
}

// Single async path for all saves. The saveActive mutex ensures that if the
// debounce and a manual save fire simultaneously only one request goes out.
// The generation check (idAtStart vs changeId) ensures that a save completing
// after newer local changes does not incorrectly clear the dirty indicator.
async function performSave() {
  if (saveActive) return;
  if (changeId.value === savedId.value) return; // already in sync, nothing to do
  const idAtStart = changeId.value;
  saveActive = true;
  isSaving.value = true;
  try {
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    await review.saveStudyDesignAttributeValue(
      props.study.id,
      props.attributeStructure.id,
      buildCurrentValue(),
    );
    skipServerLoadUntil = Date.now() + SKIP_DURATION;
    // Advance savedId to the generation we just saved. If newer changes
    // arrived while the request was in flight (changeId > idAtStart), the
    // indicator will correctly stay dirty until those changes are also saved.
    savedId.value = idAtStart;
    saveEverSucceeded.value = true;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  } finally {
    saveActive = false;
    isSaving.value = false;
  }
}

const debouncedPerformSave = useDebounceFn(performSave, 3000);

function syncToServer() {
  if (isLoadingFromServer) return;
  changeId.value++;
  debouncedPerformSave();
}

async function saveNow() {
  await performSave();
}

watch(localPopulations, syncToServer, { deep: true });
watch(localArms, syncToServer, { deep: true });
watch(localExposures, syncToServer, { deep: true });
watch(localInterventions, syncToServer, { deep: true });
watch(localGlobalCriteria, syncToServer, { deep: true });
watch(localGlobalCharacteristics, syncToServer, { deep: true });
watch(localBaselineName, syncToServer);
</script>
